import React, { useState, useEffect, useRef, useCallback } from 'react';

const Service = ({ currentLanguage = 'KM' }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('ទាំងអស់');
  const [categories, setCategories] = useState([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [imageSources, setImageSources] = useState({});
  const loadMoreRef = useRef(null);

  const defaultImage = "https://images.unsplash.com/photo-1556228570-3ce5b2ce9d2e?w=300&h=200&fit=crop&auto=format";

  // Branch Information
  const branches = [
    {
      id: 1,
      name: "សាខាទី១ - SARYDA BEAUTY CLINIC",
      hours: "9am - 2am (រាល់ថ្ងៃ)",
      phone: "077221295",
      telegram: "t.me/chhanryda",
      location: "ផ្លូវក្រោយនាគទិព្វ ផ្លូវទៅស្រាផ្ទិល ហួសនាគទិព្វប្រហែល 800 ម៉ែត្រ",
      mapLink: "https://maps.app.goo.gl/NrdU6zR31NENcwYq7?g_st=ic"
    },
    {
      id: 2,
      name: "សាខាទី២ - The King International spa and clinic",
      hours: "8:30am - 7pm (រាល់ថ្ងៃ)",
      phone: "077221295",
      location: "ផ្លូវលេខ៣ កំពង់ស្វាយ សិរីសោភ័ណ បន្ទាយមានជ័យ",
      mapLink: "https://maps.app.goo.gl/nnrnG8sCVGCD6R2LA?g_st=com.google.maps.preview.copy"
    },
    {
      id: 3,
      name: "សាខាទី៣",
      hours: "9am - 10pm (រាល់ថ្ងៃ)",
      phone: "077221295",
      location: "ក្បែរផ្សារទួលគោក, ផ្លូវលេខ 614",
      mapLink: "https://maps.app.goo.gl/U3H9JTn1LfCESJF57"
    }
  ];

  useEffect(() => {
    fetch('/services.json')
      .then((res) => res.json())
      .then((data) => {
        let processedServices = [];
        let categorySet = new Set();
        
        Object.entries(data.services).forEach(([category, categoryServices]) => {
          // Format category name for display
          const formattedCategory = category.split('. ')[1] || category;
          categorySet.add(formattedCategory);
          
          categoryServices.forEach((service) => {
            processedServices.push({
              id: service.id || `${category}-${Math.random()}`,
              title: service.service || service.name,
              description: service.bonus && service.bonus !== '-' 
                ? `ប្រាក់រង្វាន់: ${service.bonus}` 
                : "សេវាកម្មដែលមានគុណភាពខ្ពស់ជាមួយការថែទាំប្រកបដោយវិជ្ជាជីវៈ",
              detailedDescription: `សេវាកម្ម: ${service.service || service.name}. តម្លៃ: ${service.price}. ${service.bonus && service.bonus !== '-' ? `ការផ្តល់ជូនពិសេស: ${service.bonus}` : 'សេវាកម្មប្រកបដោយវិជ្ជាជីវៈជាមួយនឹងការធានាពេញចិត្ត។'}`,
              price: service.price,
              duration: "30-90 នាទី",
              image: service.image || defaultImage,
              category: formattedCategory,
              bonus: service.bonus,
              features: [
                "សេវាកម្មប្រកបដោយវិជ្ជាជីវៈ",
                "ផលិតផលដែលមានគុណភាពខ្ពស់",
                "បរិស្ថានស្អាត និងមានអនាម័យ",
                "អ្នកឯកទេសដែលមានបទពិសោធន៍"
              ],
              benefits: [
                "លទ្ធផលយូរអង្វែង",
                "មានសុវត្ថិភាព និងផាសុកភាព",
                "បទពិសោធន៍សម្រាកកាយ",
                "ការថែទាំផ្ទាល់ខ្លួន"
              ]
            });
          });
        });
        
        setAllServices(processedServices);
        setCategories(['ទាំងអស់', ...Array.from(categorySet)]);
        setImageSources(processedServices.reduce((acc, s) => {
          acc[s.id] = s.image;
          return acc;
        }, {}));
        setIsFetching(false);
      })
      .catch((error) => {
        console.error('កំហុសក្នុងការទាញយកសេវាកម្ម:', error);
        setIsFetching(false);
      });
  }, []);

  // Reset visible count when category changes
  useEffect(() => {
    setVisibleCount(10);
  }, [selectedCategory]);

  const loadMoreServices = useCallback(() => {
    const currentServices = selectedCategory === 'ទាំងអស់' 
      ? allServices 
      : allServices.filter(service => service.category === selectedCategory);
    
    if (visibleCount >= currentServices.length) {
      return;
    }

    setIsLoading(true);
    
    setTimeout(() => {
      setVisibleCount(prevCount => prevCount + 10);
      setIsLoading(false);
    }, 500);
  }, [visibleCount, allServices, selectedCategory]);

  // Intersection Observer for auto-loading
  useEffect(() => {
    const currentRef = loadMoreRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && visibleCount < (selectedCategory === 'ទាំងអស់' ? allServices.length : allServices.filter(s => s.category === selectedCategory).length)) {
          loadMoreServices();
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [visibleCount, isLoading, allServices, selectedCategory, loadMoreServices]);

  const handleImageLoad = (serviceId) => {
    setImageLoadStates(prev => ({
      ...prev,
      [serviceId]: true
    }));
  };

  const handleImageError = (serviceId) => {
    setImageLoadStates(prev => ({
      ...prev,
      [serviceId]: true
    }));
    setImageSources(prev => ({
      ...prev,
      [serviceId]: defaultImage
    }));
  };

  const socialLinks = {
    instagram: "https://www.instagram.com/kimhuotlim?igsh=cmhxYmswcjF6Nm82&utm_source=qr",
    facebook: "https://web.facebook.com/people/The-Gilded-OWL-Nail-salon-SPA/61580336022975/?_rdc=1&_rdr#",
    telegram: "https://t.me/chhanryda",
    phone: "tel:077221295"
  };

  const currentServices = selectedCategory === 'ទាំងអស់' 
    ? allServices 
    : allServices.filter(service => service.category === selectedCategory);

  const visibleServices = currentServices.slice(0, visibleCount);
  const hasMoreServices = visibleCount < currentServices.length;

  const handleBookNow = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleSocialClick = (platform) => {
    window.open(socialLinks[platform], '_blank');
  };

  const handleCallClick = () => {
    window.location.href = socialLinks.phone;
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleLoadMoreClick = () => {
    loadMoreServices();
  };

  // Get price display
  const getPriceDisplay = (price) => {
    if (typeof price === 'string') {
      return price.includes('$') ? price : `$${price}`;
    }
    return `$${price}`;
  };

  if (isFetching) {
    return (
      <section className="py-8 bg-gray-50 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">កំពុងផ្ទុកសេវាកម្ម...</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <style>{`
        .font-inter {
          font-family: 'Inter', 'Battambang', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .category-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 #f7fafc;
        }

        .category-scroll::-webkit-scrollbar {
          height: 6px;
        }

        .category-scroll::-webkit-scrollbar-track {
          background: #f7fafc;
          border-radius: 3px;
        }

        .category-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }

        .category-scroll::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }

        .loading-spinner {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #f2c742;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .image-loading {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
        }

        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .service-image {
          transition: opacity 0.3s ease;
        }

        .service-image.loading {
          opacity: 0;
        }

        .service-image.loaded {
          opacity: 1;
        }
      `}</style>

      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hotline Banner */}
          <div className="mb-8 bg-gradient-to-r from-[#f2c742] to-[#e0b53a] rounded-lg p-4 text-center">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="font-bold text-gray-900">📞 Hotline: 077221295</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-900">ម៉ោងបើក៖ 9am - 10pm (រាល់ថ្ងៃ)</span>
              </div>
              <a 
                href="https://t.me/chhanryda" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                <span>Telegram: @chhanryda</span>
              </a>
            </div>
          </div>

          {/* Category Filter Buttons */}
          <div className="mb-8">
            <div className="category-scroll flex gap-2 sm:gap-3 pb-2 sm:flex-wrap sm:justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-semibold text-sm sm:text-base whitespace-nowrap transition-all font-inter ${
                    selectedCategory === category
                      ? 'bg-[#f2c742] text-gray-900 shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {visibleServices.map((service) => (
              <div 
                key={service.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Service Image with Loading State */}
                <div className="relative h-48 sm:h-40 md:h-48 overflow-hidden">
                  {!imageLoadStates[service.id] && (
                    <div className="absolute inset-0 image-loading"></div>
                  )}
                  <img 
                    src={imageSources[service.id] || service.image} 
                    alt={service.title}
                    className={`w-full h-full object-cover service-image ${
                      imageLoadStates[service.id] ? 'loaded' : 'loading'
                    }`}
                    onLoad={() => handleImageLoad(service.id)}
                    onError={() => handleImageError(service.id)}
                  />
                  <div className="absolute top-2 left-2 bg-gray-800 text-white px-2 py-1 rounded text-xs font-semibold font-inter">
                    {service.category}
                  </div>
                  <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-[#f2c742] text-gray-900 px-2 py-1 rounded-lg text-xs md:text-sm font-semibold">
                    {getPriceDisplay(service.price)}
                  </div>
                </div>

                {/* Service Content */}
                <div className="p-3 sm:p-4 md:p-5">
                  <h3 className={`text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-2 line-clamp-2 font-inter`}>
                    {service.title}
                  </h3>
                  
                  {service.bonus && service.bonus !== '-' && (
                    <div className="mb-2">
                      <span className="inline-block bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-inter">
                        🎁 {service.bonus}
                      </span>
                    </div>
                  )}
                  
                  <p className={`hidden md:block text-gray-600 text-sm mb-3 font-inter line-clamp-2`}>
                    {service.description}
                  </p>
                  
                  <button 
                    onClick={() => handleBookNow(service)}
                    className={`w-full bg-[#f2c742] hover:bg-[#e0b53a] text-gray-900 py-2 md:py-2.5 rounded-lg text-xs sm:text-sm md:text-base font-medium font-inter transition-colors`}
                  >
                    កក់ទុកឥឡូវនេះ
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* No Results Message */}
          {currentServices.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg font-inter">
                មិនមានសេវាកម្មនៅក្នុងប្រភេទនេះទេ។
              </p>
            </div>
          )}

          {/* Load More Section */}
          {hasMoreServices && (
            <div className="mt-12 text-center">
              {/* Auto-load trigger element */}
              <div ref={loadMoreRef} className="h-10"></div>
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="mb-6">
                  <div className="loading-spinner"></div>
                  <p className="text-gray-600 mt-2 font-inter">
                    កំពុងផ្ទុកសេវាកម្មបន្ថែម...
                  </p>
                </div>
              )}

              {/* Manual load more button */}
              {!isLoading && (
                <button
                  onClick={handleLoadMoreClick}
                  className="bg-[#f2c742] hover:bg-[#e0b53a] text-gray-900 px-8 py-3 rounded-lg font-semibold font-inter transition-colors"
                >
                  ផ្ទុកបន្ថែម ({currentServices.length - visibleCount} នៅសល់)
                </button>
              )}
            </div>
          )}

          {/* End of results message */}
          {!hasMoreServices && currentServices.length > 0 && (
            <div className="text-center mt-12">
              <p className="text-gray-500 font-inter">
                អ្នកបានឃើញសេវាកម្មទាំងអស់ {currentServices.length} នៅក្នុងប្រភេទនេះហើយ។
              </p>
            </div>
          )}

          {/* Branch Information Section */}
          <div className="mt-16">
            <h3 className={`text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 font-inter`}>
              សាខារបស់យើង
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {branches.map((branch) => (
                <div key={branch.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-[#f2c742] bg-opacity-20 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-[#f2c742]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <h4 className={`font-bold text-gray-900 font-inter`}>
                      {branch.name}
                    </h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-600 font-inter">{branch.hours}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-gray-600 font-inter text-xs">{branch.location}</span>
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <a 
                        href={`tel:${branch.phone}`}
                        className="flex items-center gap-1 text-[#f2c742] hover:text-[#e0b53a] font-semibold"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {branch.phone}
                      </a>
                      {branch.telegram && (
                        <a 
                          href={branch.telegram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#f2c742] hover:text-[#e0b53a] font-semibold"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                          </svg>
                          Telegram
                        </a>
                      )}
                    </div>
                    <a 
                      href={branch.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-sm text-[#f2c742] hover:text-[#e0b53a] font-semibold"
                    >
                      📍 មើលផែនទី →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      {isModalOpen && selectedService && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0  bg-opacity-50" 
            onClick={closeModal}
          ></div>
          
          <div className="flex items-center justify-center min-h-full p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-50 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Scrollable Content */}
              <div className="overflow-y-auto max-h-[90vh]">
                {/* Header with Image */}
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img 
                    src={imageSources[selectedService.id] || selectedService.image} 
                    alt={selectedService.title}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(selectedService.id)}
                  />
                  <div className="absolute inset-0  bg-opacity-40"></div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h2 className={`text-xl md:text-3xl font-bold mb-2 font-inter`}>
                      {selectedService.title}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="bg-[#f2c742] text-gray-900 px-3 py-1 rounded-lg text-sm font-semibold">
                        {getPriceDisplay(selectedService.price)}
                      </span>
                      <span className="flex items-center text-sm">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {selectedService.duration}
                      </span>
                      <span className="inline-block bg-gray-800 text-white px-2 py-1 rounded text-sm font-inter">
                        {selectedService.category}
                      </span>
                    </div>
                    {selectedService.bonus && selectedService.bonus !== '-' && (
                      <div className="mt-2">
                        <span className="inline-block bg-green-500 text-white px-3 py-1 rounded-lg text-sm font-semibold">
                          🎁 ប្រាក់រង្វាន់: {selectedService.bonus}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div>
                      <h3 className={`text-xl font-semibold text-gray-900 mb-3 font-inter`}>
                        ការពិពណ៌នាសេវាកម្ម
                      </h3>
                      <p className={`text-gray-600 mb-6 font-inter`}>
                        {selectedService.detailedDescription}
                      </p>

                      <h4 className={`text-lg font-semibold text-gray-900 mb-3 font-inter`}>
                        អ្វីដែលរួមបញ្ចូល៖
                      </h4>
                      <div className="space-y-2 mb-6">
                        {selectedService.features.map((feature, index) => (
                          <div key={index} className="flex items-center">
                            <svg className="w-5 h-5 text-[#f2c742] mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className={`text-gray-700 font-inter`}>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right Column */}
                    <div>
                      <h4 className={`text-lg font-semibold text-gray-900 mb-3 font-inter`}>
                        អត្ថប្រយោជន៍៖
                      </h4>
                      <div className="space-y-2 mb-6">
                        {selectedService.benefits.map((benefit, index) => (
                          <div key={index} className="flex items-center">
                            <svg className="w-5 h-5 text-[#f2c742] mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className={`text-gray-700 font-inter`}>{benefit}</span>
                          </div>
                        ))}
                      </div>

                      {/* Contact Buttons */}
                      <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                        <h4 className={`text-lg font-semibold text-gray-900 mb-3 text-center font-inter`}>
                          កក់សេវាកម្មនេះ
                        </h4>
                        <p className={`text-sm text-gray-600 mb-4 text-center font-inter`}>
                          ទាក់ទងមកយើងខ្ញុំដើម្បីកំណត់កាលវិភាគ
                        </p>
                        
                        <div className="space-y-3">
                          {/* Call Button */}
                          <button 
                            onClick={handleCallClick}
                            className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span className="font-inter">ទូរស័ព្ទ 077221295</span>
                          </button>

                          {/* Telegram Button */}
                          <button 
                            onClick={() => handleSocialClick('telegram')}
                            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                            </svg>
                            <span className="font-inter">Telegram: @chhanryda</span>
                          </button>

                          
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Service;